// AudioMessage.jsx
import React, { useRef, useEffect, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';

const PlayIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="w-5 h-5 text-white">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"></path>
    </svg>
);
const PauseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="w-5 h-5 text-white">
        <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0a.75.75 0 01.75-.75h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75h-1.5a.75.75 0 01-.75-.75V5.25z" clipRule="evenodd" />
    </svg>
);
const LoadingIcon = () => (
     <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);


const AudioMessage = ({
    audioUrl,
    waveColor = '#D1D5DB',
    progressColor = '#A5B4FC',
    buttonBg = 'bg-indigo-300',
    buttonActiveBg = 'active:bg-indigo-400',
}) => {
    const waveformRef = useRef(null);
    const wavesurferRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [duration, setDuration] = useState('00:00');
    const [currentTime, setCurrentTime] = useState('00:00');


    const formatTime = useCallback((seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;
        return `${formattedMinutes}:${formattedSeconds}`;
    }, []);


    useEffect(() => {
        const shouldLoadAudio = true;
        const waveformContainer = waveformRef.current;

        if (!waveformContainer) {
             if (wavesurferRef.current) {
                 wavesurferRef.current.destroy();
                 wavesurferRef.current = null;
             }
             return;
         }

        if (wavesurferRef.current) {
             wavesurferRef.current.destroy();
             wavesurferRef.current = null;
         }


        setIsLoading(shouldLoadAudio);
        setCurrentTime('00:00');
        setDuration('00:00');


        const ws = WaveSurfer.create({
            container: waveformContainer,
            waveColor: waveColor,
            progressColor: progressColor,
            cursorWidth: 0,
            barWidth: 2,
            barGap: 2,
            barRadius: 3,
            height: 30,
            mediaControls: false,
            interact: shouldLoadAudio,
            normalize: true,
        });
        wavesurferRef.current = ws;


        if (shouldLoadAudio) {
            ws.on('ready', () => {
                setIsLoading(false);
                setDuration(formatTime(ws.getDuration()));
                setCurrentTime(formatTime(0));
            });
            ws.on('play', () => setIsPlaying(true));
            ws.on('pause', () => setIsPlaying(false));
            ws.on('finish', () => {
                setIsPlaying(false);
                ws.seekTo(0);
                setCurrentTime(formatTime(0));
            });
            ws.on('audioprocess', () => {
                setCurrentTime(formatTime(ws.getCurrentTime()));
            });
            ws.on('seeking', () => {
                 setCurrentTime(formatTime(ws.getCurrentTime()));
             });
            ws.on('error', (err) => {
                console.error("Wavesurfer error:", err);
                setIsLoading(false);
                setDuration('Error');
                 if (wavesurferRef.current) {
                     wavesurferRef.current.destroy();
                     wavesurferRef.current = null;
                 }
                 setIsPlaying(false);
             });
        }


        if (shouldLoadAudio && audioUrl) {
             ws.load(audioUrl).catch(error => {
                 if (error.name === 'AbortError') {
                     console.log("Wavesurfer load aborted (expected cleanup).");
                 } else {
                     console.error("Wavesurfer load error (other):", error);
                      setIsLoading(false);
                      setDuration('Error');
                 }
             });
        } else {
            setIsLoading(false);
        }


        return () => {
             if (wavesurferRef.current) {
                  wavesurferRef.current.destroy();
                  wavesurferRef.current = null;
             }
        };
    }, [audioUrl, waveColor, progressColor, formatTime, setIsLoading, setDuration, setIsPlaying, setCurrentTime]);


    const handlePlayPause = useCallback(() => {
        if (!isLoading && wavesurferRef.current) {
             wavesurferRef.current.playPause();
        }
    }, [isLoading]);


    return (
        <div className="flex items-center outline-none" tabIndex={0} aria-label="Audio message">
            <button
                onClick={handlePlayPause}
                disabled={isLoading}
                className={`p-4 mr-4 flex-shrink-0 flex justify-center items-center rounded-[.75rem] outline-none transition-all duration-200 ${buttonBg} ${buttonActiveBg} ${isLoading ? 'cursor-wait opacity-70' : ''}`}
                aria-label={isPlaying ? "Pause audio" : "Play audio"}
            >
                {isLoading ? <LoadingIcon /> : isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>

            <div className="w-[9.375rem] mr-4 relative flex items-center h-[30px]">
                <div ref={waveformRef} className="w-full h-full"></div>
                {isLoading && (
                    <div className="absolute inset-0 border border-gray-300 dark:border-gray-500 rounded animate-pulse"></div>
                )}
            </div>

            <p
                className="flex-shrink-0 outline-none text-xs font-light leading-4 tracking-[.01rem] text-black dark:text-white opacity-40 dark:opacity-70 w-12 text-right"
                aria-label={`Audio time ${currentTime} of ${duration}`}
            >
                 { isPlaying ? currentTime : duration }
            </p>
        </div>
    );
};

export { AudioMessage };